import { useEffect, useMemo, useRef } from "react";
import { CircleMarker, MapContainer, Polyline, TileLayer, useMap } from "react-leaflet";
import type { LatLngBoundsExpression, LatLngExpression, LeafletMouseEvent } from "leaflet";
import type { RidePoint } from "../types/ride";
import { useTheme } from "../theme/ThemeProvider";
import { useHealth } from "../hooks/useApi";
import { getTileConfig } from "../lib/mapTiles";

function FitBounds({ bounds }: { bounds: LatLngBoundsExpression | null }) {
  const map = useMap();
  useEffect(() => {
    if (bounds) map.fitBounds(bounds, { padding: [24, 24], maxZoom: 18 });
  }, [map, bounds]);
  return null;
}

/** Index of the fixed-point array entry whose lat/lon is nearest to the given latlng. */
function nearestFixedIndex(fixed: { index: number; lat: number; lon: number }[], lat: number, lon: number): number {
  let best = fixed[0]!.index;
  let bestDist = Infinity;
  for (const f of fixed) {
    const d = (f.lat - lat) ** 2 + (f.lon - lon) ** 2;
    if (d < bestDist) {
      bestDist = d;
      best = f.index;
    }
  }
  return best;
}

export function RideMap({
  points,
  cursorIndex,
  onCursorChange,
}: {
  points: RidePoint[];
  cursorIndex: number;
  onCursorChange: (index: number) => void;
}) {
  const { resolved } = useTheme();
  const { data: health } = useHealth();
  const tileConfig = getTileConfig(health?.mapTiles.provider ?? "esri", health?.mapTiles.apiKey ?? null, resolved);

  const fixed = useMemo(
    () =>
      points
        .map((p, index) => ({ index, lat: p.lat, lon: p.lon }))
        .filter((p): p is { index: number; lat: number; lon: number } => p.lat !== null && p.lon !== null),
    [points],
  );

  const path: LatLngExpression[] = useMemo(() => fixed.map((f) => [f.lat, f.lon]), [fixed]);

  const bounds: LatLngBoundsExpression | null = useMemo(() => {
    if (fixed.length === 0) return null;
    const lats = fixed.map((f) => f.lat);
    const lons = fixed.map((f) => f.lon);
    return [
      [Math.min(...lats), Math.min(...lons)],
      [Math.max(...lats), Math.max(...lons)],
    ];
  }, [fixed]);

  const fixedRef = useRef(fixed);
  fixedRef.current = fixed;

  const handleClick = (e: LeafletMouseEvent) => {
    if (fixedRef.current.length === 0) return;
    onCursorChange(nearestFixedIndex(fixedRef.current, e.latlng.lat, e.latlng.lng));
  };

  const cursorPoint = points[cursorIndex];
  const cursorLatLng: LatLngExpression | null =
    cursorPoint && cursorPoint.lat !== null && cursorPoint.lon !== null ? [cursorPoint.lat, cursorPoint.lon] : null;

  if (fixed.length === 0) {
    return (
      <div className="flex h-full min-h-[280px] items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-500 dark:border-night-800 dark:bg-night-900 dark:text-night-400">
        No GPS fix recorded for this ride.
      </div>
    );
  }

  return (
    <div className="h-full min-h-[280px] overflow-hidden rounded-xl">
      <MapContainer center={path[0]} zoom={16} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
        <TileLayer
          key={`${tileConfig.url}-${resolved}`}
          attribution={tileConfig.attribution}
          url={tileConfig.url}
          maxNativeZoom={tileConfig.maxNativeZoom}
        />
        <FitBounds bounds={bounds} />
        <Polyline positions={path} pathOptions={{ color: "#0398f0", weight: 4 }} eventHandlers={{ click: handleClick }} />
        {cursorLatLng && (
          <CircleMarker
            center={cursorLatLng}
            radius={8}
            pathOptions={{ color: "#ffffff", weight: 2, fillColor: "#0161a7", fillOpacity: 1 }}
          />
        )}
      </MapContainer>
    </div>
  );
}
